import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { AuthService } from '@services/auth.service';
import { MIN_PASSWORD_LENGTH } from '@common/validators/password.schema';
import { ThemeService } from '@services/theme.service';
import { NotificationService } from '@services/notification.service';
import { RealtimeService } from '@services/realtime.service';
import { NAV_SECTIONS, NavItem, NavSection, Permission } from '@common/constants';
import { IncidentService } from '@incident/services/incident.service';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NotificationPanelComponent } from '@components/notification-panel/notification-panel.component';
import { CompanyMarkComponent } from '@components/company-mark/company-mark.component';

const SIDEBAR_COLLAPSED_KEY = 'ptp-sidebar-collapsed';

@Component({
  selector: 'app-layout',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NzAlertModule,
    NzAvatarModule,
    NzBadgeModule,
    NzButtonModule,
    NzDropdownModule,
    NzIconModule,
    NzMenuModule,
    NzTooltipModule,
    NotificationPanelComponent,
    CompanyMarkComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  protected authService = inject(AuthService);
  protected readonly minPasswordLength = MIN_PASSWORD_LENGTH;
  protected theme = inject(ThemeService);
  private notificationService = inject(NotificationService);
  protected realtime = inject(RealtimeService);
  private destroyRef = inject(DestroyRef);
  private incidentService = inject(IncidentService);
  private router = inject(Router);

  /** The signed-in user's company: its display name and logo, or PTP's own until they load. */
  protected readonly brandName = computed(
    () => this.authService.user()?.company?.name ?? 'PTP Fleet',
  );
  protected readonly brandLogoUrl = computed(
    () => this.authService.user()?.company?.logoUrl ?? null,
  );

  /** icon-rail state — persisted the same way ThemeService persists theme mode */
  protected readonly collapsed = signal<boolean>(this.initialCollapsed());

  /** the toggle button's label — what clicking it does, not the current state */
  protected readonly collapseLabel = computed(() =>
    this.collapsed() ? 'Expand sidebar' : 'Collapse sidebar',
  );

  /** whether the bell's dropdown is open — gates the panel's own fetch/poll */
  protected readonly notifOpen = signal(false);
  protected readonly unreadCount = signal(0);
  /** Rings the badge once when the count goes up; cleared by the animation's own timer. */
  protected readonly badgePulse = signal(false);
  protected readonly bellLabel = computed(() =>
    this.realtime.paused() ? 'Notifications — live updates paused' : 'Notifications',
  );
  /** reports still waiting for an operator; shown on the Incidents item */
  protected readonly openIncidents = signal(0);

  constructor() {
    effect(() => {
      const c = this.collapsed();
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, c ? '1' : '0');
      } catch {
        /* private mode — the in-memory signal still drives the UI */
      }
    });

    // Asked for once, when the layout opens. Nothing refreshes them on a timer;
    // marking notifications read refreshes the bell badge, and a reload the rest.
    this.refreshUnreadCount();
    this.refreshOpenIncidents();

    // The server says "something changed"; the count is then re-read over REST.
    // A reconnect or a long-hidden tab may have missed events, so re-read then too.
    this.realtime.start();
    this.destroyRef.onDestroy(() => this.realtime.stop());
    this.realtime.notificationsChanged$
      .pipe(takeUntilDestroyed())
      // only a live "created" push rings the badge; loading or catching up must not
      .subscribe((event) => this.refreshUnreadCount(event?.action === 'created'));
  }

  protected onNotifVisibleChange(visible: boolean): void {
    this.notifOpen.set(visible);
  }

  protected refreshUnreadCount(ring = false): void {
    this.notificationService.getUnreadCount().subscribe({
      next: (count) => {
        if (ring && count > this.unreadCount()) {
          this.ringBadge();
        }
        this.unreadCount.set(count);
      },
      error: () => undefined, // a failed poll leaves the badge as it was
    });
  }

  private ringBadge(): void {
    this.badgePulse.set(false);
    // a frame later, so a second ring restarts the animation instead of being ignored
    requestAnimationFrame(() => {
      this.badgePulse.set(true);
      setTimeout(() => this.badgePulse.set(false), 1_100);
    });
  }

  /** Only asked for when the user may read incidents; a failure just leaves the old count. */
  protected refreshOpenIncidents(): void {
    if (!this.authService.hasPermission(Permission.Incident.Read)) {
      return;
    }
    this.incidentService.countOpen().subscribe({
      next: (page) => this.openIncidents.set(page.total),
      error: () => undefined,
    });
  }

  protected badgeFor(item: NavItem): number {
    return item.badge === 'openIncidents' ? this.openIncidents() : 0;
  }

  protected readonly sections: NavSection[] = NAV_SECTIONS;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly user = computed(() => this.authService.user());

  protected readonly fullName = computed(() => {
    const u = this.user();
    return u ? `${u.firstName} ${u.lastName}`.trim() : '';
  });

  protected readonly pageTitle = computed(() => {
    // Query string and fragment are not part of the route: a page that keeps its
    // filters in the URL (e.g. /audit-trail?action=login) must keep its title.
    const u = this.url().split(/[?#]/)[0];
    const match = this.sections
      .flatMap((s) => s.items)
      .filter((it) => it.path && (u === `/${it.path}` || u.startsWith(`/${it.path}/`)))
      .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];
    return match?.label ?? 'PTP Fleet';
  });

  protected canSee(item: NavItem): boolean {
    if (item.soon) {
      return true;
    }
    if (item.anyPermission) {
      return item.anyPermission.some((p) => this.authService.hasPermission(p));
    }
    return !item.permission || this.authService.hasPermission(item.permission);
  }

  protected sectionVisible(section: NavSection): boolean {
    return section.items.some((it) => this.canSee(it));
  }

  protected getInitialName(): string {
    const u = this.authService.user();
    return `${u?.firstName?.charAt(0) ?? ''}${u?.lastName?.charAt(0) ?? ''}`;
  }

  protected logout(): void {
    this.authService.logout();
  }

  protected toggleCollapsed(): void {
    this.collapsed.update((c) => !c);
  }

  private initialCollapsed(): boolean {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  }
}
